import { useEffect, useState, useCallback } from "react";
import { ChefHat, Clock, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import BentoSlotSkeleton from "./BentoSlotSkeleton";

/**
 * Proactive "Expiring soon" widget that consumes
 * ``GET /production-logs/expiring?days=3`` and surfaces the next partidas
 * of the kitchen mise en place before they spoil. Self-hides when there
 * are no expiring items. Persistent dismiss removed (Feb 2026) — users
 * can permanently hide the widget via "Editar panel".
 */
export default function ExpiringProductionWidget({ restaurantId, daysAhead = 3 }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!restaurantId) return;
    setLoading(true);
    try {
      const data = await api.getExpiringProductionItems(restaurantId, daysAhead);
      setItems(data.items || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [restaurantId, daysAhead]);

  useEffect(() => { load(); }, [load]);

  // Reserve slot while loading so the bento grid doesn't reflow.
  if (loading) return <BentoSlotSkeleton height={64} />;
  if (!items.length) return null;

  const today = new Date().toISOString().slice(0, 10);
  const tomorrow = new Date(Date.now() + 86400_000).toISOString().slice(0, 10);

  const hasCritical = items.some(r => (r.item?.expires_at || "") <= tomorrow);

  return (
    <Card
      className={`border ${hasCritical ? "border-rose-200 bg-rose-50/60" : "border-amber-200 bg-amber-50/60"}`}
      data-testid="expiring-production-widget"
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-3">
          <div className={`p-2 rounded-lg shrink-0 ${hasCritical ? "bg-rose-100" : "bg-amber-100"}`}>
            <ChefHat className={`h-5 w-5 ${hasCritical ? "text-rose-700" : "text-amber-700"}`} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold text-sm ${hasCritical ? "text-rose-900" : "text-amber-900"}`}>
              {items.length} preparaci{items.length === 1 ? "ón" : "ones"} próxima{items.length === 1 ? "" : "s"} a caducar
            </h3>
            <p className={`text-[11px] mb-2 ${hasCritical ? "text-rose-700" : "text-amber-700"}`}>
              Usa o marca como merma antes de que caduquen. Próximas {daysAhead} días.
            </p>
            <div className="flex flex-wrap gap-1.5">
              {items.slice(0, 8).map(r => {
                const it = r.item || {};
                const exp = it.expires_at || "";
                const isToday = exp === today;
                const isTomorrow = exp === tomorrow;
                const tone = isToday
                  ? "bg-rose-100 text-rose-900 border-rose-300"
                  : isTomorrow
                    ? "bg-amber-100 text-amber-900 border-amber-300"
                    : "bg-card text-foreground border-border";
                return (
                  <span
                    key={`${r.log_id}-${it.item_id}`}
                    className={`inline-flex items-center gap-1 text-[11px] px-2 py-0.5 rounded border ${tone}`}
                    data-testid={`expiring-chip-${it.item_id}`}
                  >
                    {isToday && <AlertTriangle className="h-3 w-3" />}
                    {!isToday && <Clock className="h-3 w-3" />}
                    <b>{it.name}</b>
                    <span className="opacity-75">·</span>
                    <span>{it.qty} {it.unit}</span>
                    <span className="opacity-75">·</span>
                    <span>
                      {isToday ? "caduca HOY" : isTomorrow ? "caduca mañana" : exp}
                    </span>
                  </span>
                );
              })}
              {items.length > 8 && (
                <span className="text-[11px] text-muted-foreground self-center">
                  +{items.length - 8} más
                </span>
              )}
            </div>
            <div className="mt-2">
              <Button asChild size="sm" variant="outline" data-testid="expiring-goto-closings">
                <Link to="/app/finance">
                  Ir a Cierres de producción
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
